import { createContext } from '@lit/context';
import { DEFAULT_PAYMENT_CURRENCY } from '@yebo/shared';
import type { PaymentProvider, WorkingHoursSchedule, DepositConfig, PublicEvent } from '@yebo/shared';
import { naiveLocalIso, anchorForDisplay, formatFixedWhen, parseIsoDate, parseHhmm } from '../utils/timezone.js';

export type BookingStep = 'service' | 'datetime' | 'contact' | 'payment' | 'confirm';

/**
 * Theme returned by GET /api/widget/config. Applied to the host CSS custom
 * properties (`--booking-*`) so a tenant's configured branding reaches the
 * widget. All fields optional: a missing field leaves its var unset so the
 * tokens.css.ts default takes over. Mirrors the ordering widget's OrderingTheme.
 */
export interface WidgetTheme {
  primaryColor?: string;
  borderRadius?: string;
  fontFamily?: string;
  backgroundColor?: string;
  sidebarBackgroundColor?: string;
  sidebarTextColor?: string;
  textColor?: string;
  textMutedColor?: string;
  // #653 — per-surface event/ticket colours; absent unless the venue set one.
  eventHeadingColor?: string;
  eventDescriptionColor?: string;
  eventPriceColor?: string;
  borderColor?: string;
}

/**
 * #793 — the venue's per-field Off / Optional / Required choice for the three
 * optional details the PUBLIC widget can ask for. Off means the widget never
 * renders the control and never sends a value; Required means the step refuses
 * an empty one. Mirrors the staff-side control added by #792 (ruling R13).
 */
export type WidgetFieldState = 'off' | 'optional' | 'required';

/** The three Settings-controlled fields that exist in the widget's contact step. */
export type WidgetFieldKey = 'dietary' | 'seatingPreference' | 'occasion';

export type WidgetFieldStates = Record<WidgetFieldKey, WidgetFieldState>;

export const WIDGET_FIELD_KEYS: WidgetFieldKey[] = ['dietary', 'seatingPreference', 'occasion'];

/**
 * The dark state (#793): a venue that never touched the switch — and every
 * older server whose /config carries no `fields` block — behaves exactly as
 * today, i.e. all three optional. Frozen so a caller cannot mutate the default.
 */
export const DEFAULT_FIELD_STATES: WidgetFieldStates = Object.freeze({
  dietary: 'optional',
  seatingPreference: 'optional',
  occasion: 'optional',
}) as WidgetFieldStates;

/**
 * Normalise the public config's `fields` block. Anything that is not one of the
 * three literals — a missing block, a bogus value, a non-object — resolves to
 * `optional` for that key, never to `off`: a parsing accident must not silently
 * stop collecting a detail the venue asked for, and must not fabricate a
 * requirement either.
 */
export function normalizeFieldStates(raw: unknown): WidgetFieldStates {
  const out: WidgetFieldStates = { ...DEFAULT_FIELD_STATES };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const src = raw as Record<string, unknown>;
  for (const key of WIDGET_FIELD_KEYS) {
    const v = src[key];
    if (v === 'off' || v === 'optional' || v === 'required') out[key] = v;
  }
  return out;
}

/**
 * The state of one field, read defensively: a state object built before #793
 * (or a partial test fixture) has no `fieldStates`, and must read as optional.
 */
export function fieldStateOf(
  state: Pick<BookingState, 'fieldStates'> | { fieldStates?: WidgetFieldStates },
  key: WidgetFieldKey,
): WidgetFieldState {
  return state?.fieldStates?.[key] ?? DEFAULT_FIELD_STATES[key];
}

export interface BookingState {
  currentStep: BookingStep;
  locationId: string;
  calendarId: string;
  timezone: string;
  businessName: string;  // venue name — used in Add-to-Calendar (R10.7)

  // Branding theme from GET /api/widget/config, applied to host CSS vars. Null
  // until config loads, or when the config carries no theme.
  theme: WidgetTheme | null;

  // Step 1 — Service
  // MVP-4 — explicit two-path entry. `null` means the guest has not yet chosen a
  // path, so (when events exist) the service step shows the two-card chooser:
  //   'table'  → a regular party-size booking (no events section)
  //   'events' → the special-events list (party-size/ticketing appear per the
  //              existing behavior once an event is selected)
  // When no events exist there is no chooser and the flow behaves as if 'table'
  // (the field is left null and treated as the table path). Cleared to null on
  // reset and by the chooser's Back affordance.
  bookingMode: 'table' | 'events' | null;
  partySize: number;
  /** #591 — venue's online party cap (0 = none configured; UI default applies). */
  maxOnlinePartySize: number;
  /** #592 — guest-facing contact details for the "please phone us" prompts. */
  contactPhone: string;
  contactEmail: string;
  kidsCount: number;

  // #159 — special events the guest may book
  events: PublicEvent[];
  selectedEventId: string | null;
  selectedEventName: string | null;

  // #130 — recurring "standard experiences". A recurring series surfaces as ONE
  // event card (PublicEvent.recurring); selecting it does NOT fix a date. Instead
  // the guest picks a bookable occurrence in the datetime step, which resolves the
  // opaque occurrence id `${seriesId}#${date}`. That occId — never the seriesId —
  // is what the quote / checkout / book sites send. The list loads lazily when the
  // date step opens (see `fetchOccurrences`). One-off events leave all this null/[].
  experienceOccurrences: Array<{ occId: string; date: string; coversRemaining: number; soldOut?: boolean }>;
  occurrencesLoading: boolean;
  selectedOccurrenceId: string | null;   // opaque `${seriesId}#${date}` — sent verbatim, never split
  selectedOccurrenceDate: string | null; // 'YYYY-MM-DD' the chosen occurrence falls on

  // Prepaid event ticketing — populated only when the selected event carries
  // `ticketing`. A ticketed event is a fixed-price prepaid cover purchase: the
  // date is the event's date, payment is always required, and the booking is
  // gated on remaining covers. See `isTicketedFlow` / `selectedEvent`.
  selectedTierId: string | null;
  ticketQuote: {
    /** Absent for a MIXED basket — there is no single unit price to show. */
    unitPriceCents: number | null;
    /** #816 — pre-discount ticket total, from the server. ABSENT with no coupon,
     *  so a full-price quote keeps the exact shape it had before #816. */
    subtotalCents?: number | null;
    /** #816 — money taken off by a promo code, from the server. Absent when none. */
    discountCents?: number | null;
    kidsUnitPriceCents: number | null;
    kidsCount: number;
    totalCents: number;
    currency: string;
    tierLabel: string | null;
    /** #652 — the per-line breakdown, so the guest can see what makes up the
     *  total. `totalCents` remains the only number that must match the charge. */
    lines?: Array<{
      tierId: string;
      tierLabel?: string;
      qty: number;
      kidsQty: number;
      unitPriceCents: number;
      kidsUnitPriceCents: number;
      subtotalCents: number;
    }>;
  } | null;
  ticketAvailability: {
    capacity: number;
    coversSold: number;
    remaining: number;
    soldOut: boolean;
  } | null;
  ticketQuoteError: string | null;     // user-facing reason a quote was rejected
  /**
   * #816 — the guest-facing promo code.
   *
   * `couponCode` is what is IN THE FIELD (normalised: trimmed + uppercased). It
   * survives a refusal on purpose, so a typo can be corrected without retyping.
   * `couponApplied` is what the SERVER accepted — the only source of the
   * discount the guest is shown, and the only code sent on checkout. The widget
   * computes no money: `percentOff` is a label, `discountCents` is the server's.
   * `ticketsRemaining: null` means UNKNOWN — never "unlimited".
   */
  couponCode: string;
  couponApplied: {
    code: string;
    percentOff: number;
    discountCents: number;
    ticketsRemaining: number | null;
    /** #954 — the server's code-free label ("Buy 1 Get 1 Free", "10% off"). */
    label?: string;
    /** #954 — 'percent' | 'pairs', as the server priced it. */
    kind?: string;
  } | null;
  couponError: string | null;          // the SERVER's sentence, rendered verbatim
  couponApplying: boolean;
  /**
   * #954 — the event's AUTO deal (e.g. Buy 1 Get 1 Free), exactly as the last
   * quote described it. Never a typed code: it is code-free to the guest and is
   * never sent back as `couponCode`. `null` ⇒ the event has no live auto deal.
   *  - `applied: true`  the quote said `autoApplied`; `label` + `discountCents`
   *                     are the server's (rendered verbatim, never derived).
   *  - `applied: false` a live deal that did not fit the basket; `message` is
   *                     the server's sentence (nudge / explainer / exhausted).
   * Checkout sends `autoApplied: true` only when `applied` — a tripwire the
   * server uses to refuse (409 AUTO_DEAL_CHANGED), never to grant a discount.
   */
  autoDeal: {
    applied: boolean;
    label: string;
    discountCents: number;
    reason: string | null;
    message: string | null;
  } | null;
  /**
   * #954 — the discount line for the confirmation, from the server's CHECKOUT
   * echo (`discountCents` + `autoApplied` / `couponCode`). `null` when the
   * order was charged in full.
   */
  ticketDiscountLine: { label: string; discountCents: number } | null;
  // #651 — the event menu-PDF download. Transient UI state for the button;
  // the presigned URL itself is never stored (it expires in ~2 minutes).
  menuPdfBusy: boolean;
  menuPdfError: string | null;
  /**
   * #652 — a mixed-tier basket: N tickets of each type in ONE booking.
   *
   * Empty for a non-tiered event and for the legacy single-select path. When
   * non-empty it is AUTHORITATIVE over `partySize`/`kidsCount`, which are
   * derived from it (Σ qty and Σ kidsQty) rather than tracked separately —
   * two sources for one quantity is how they drift.
   */
  ticketLines: Array<{ tierId: string; qty: number; kidsQty: number }>;
  ticketReservationId: string | null;  // held seat returned by /ticket/checkout
  ticketDate: string | null;           // date the seat is held for (from checkout)

  // Step 2 — Date/Time
  selectedDate: string | null;  // YYYY-MM-DD
  selectedSlot: string | null;  // ISO 8601 start time
  selectedSlotEnd: string | null;
  availableSlots: Array<{ start: string; end: string; available: boolean }>;
  /** #947 — the venue's default booking length (minutes), from config. Used to
   *  end a fixed-date event's slot when the event carries no `end` of its own —
   *  the same fallback the availability grid uses (`cfg.defaultDuration || 90`). */
  defaultDuration: number;

  // R10.4 — empty-state recovery. Populated from the (optionally) enriched
  // `GET /api/widget/availability` payload when the chosen date has no bookable
  // slots. All fields degrade gracefully to empty/null when the backend has not
  // yet shipped the enrichment (View 5 · R5.1) — the widget never depends on
  // their presence. See `EnrichedAvailabilityResponse` in api/client.ts.
  availabilityReason: string | null;          // why the date is empty (typed)
  alternateTimes: Array<{ start: string; end: string; available: boolean; sectionLabel?: string }>;
  alternateSections: Array<{ sectionId: string; sectionLabel: string; nextAvailable?: string }>;
  nextAvailableDate: string | null;           // "try another date" affordance

  // Step 3 — Contact
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  notes: string;
  dietaryRestrictions: string;
  highChairNeeded: boolean;
  dietaryDetails: string;
  consentGiven: boolean;

  /**
   * #793 — the venue's Off / Optional / Required per field, from the public
   * `/config` payload's `fields` block. Defaults to all-optional, which is
   * today's behaviour byte-for-byte; an `off` field is neither rendered nor
   * sent (the server drops it too — belt and braces).
   */
  fieldStates: WidgetFieldStates;

  // Step 4 — Payment
  //
  // #480 — the guest-facing deposit rail (design §5.2). `depositCheckout` is the
  // server's public-config answer to "does this venue take deposits through
  // POST /deposit/checkout?" (default false = the dark state, §11). It is
  // ORTHOGONAL to `requirePayment`, whose truth table is unchanged (§4.1).
  //
  // NOTE (D6): there is deliberately no `depositProvider`. The widget renders by
  // HANDLE SHAPE — `paymentClientSecret` ⇒ card fields, `depositAuthorizationUrl`
  // ⇒ hosted redirect — never by provider name. A provider label is diagnostic
  // only and must not reach the render path (that coupling was break #4).
  depositCheckout: boolean;
  depositRequired: boolean;
  depositType: 'security_hold' | 'experience' | null;
  depositAmountCents: number;
  /** Reservation held `pending` by /deposit/checkout — what /deposit/verify settles. */
  depositReservationId: string | null;
  /** YYYY-MM-DD the seat is held for (the reservation's sort-key date). */
  depositDate: string | null;
  /** ISO hold deadline, VERBATIM from the server. Never derived client-side (R6). */
  depositExpiresAt: string | null;
  /** Hosted-gateway payment page (PayFast/Paystack/Square). '' when card fields apply. */
  depositAuthorizationUrl: string;
  /** Server's honest note about what its rail does with the money (§2.1, R3). */
  depositPolicyNote: string | null;

  requirePayment: boolean;
  paymentProvider: PaymentProvider;
  stripePublicKey: string;
  paymentAmount: number;            // smallest currency unit (cents)
  paymentCurrency: string;          // e.g. "usd", "zar"
  paymentIntentId: string | null;
  paymentClientSecret: string | null;
  paymentStatus: 'idle' | 'pending' | 'succeeded' | 'failed';

  // Per-person deposit config
  depositConfig: DepositConfig | null;

  // Working hours schedules (for calendar grey-out)
  workingHoursSchedules: WorkingHoursSchedule[];

  // GHL payment
  ghlOrderId: string | null;
  ghlPaymentUrl: string | null;

  // Idempotency key for the booking submit (dedupes retries — #290)
  idempotencyKey: string;

  // Result
  appointmentId: string | null;
  contactId: string | null;
  confirmationNumber: string | null;

  // R10.6 — Join Notify (cancellation-recovery demand list). Set once the guest
  // submits the notify form from the empty state; surfaces a success message.
  notifySubmitting: boolean;
  notifySubmitted: boolean;
  notifyError: string | null;

  // Sibling-venue cross-sell (R10.9) — surfaced in the empty state when the
  // tenant has configured a sibling location. Read from the config allowlist;
  // absent for single-venue tenants.
  siblingVenue: { name: string; url: string } | null;

  // R10.7 — tokened self-service manage-booking link, issued by the booking
  // create/checkout response. Empty when the backend has not yet issued one.
  manageBookingUrl: string;

  // R10.8 — progressive disclosure (accessibility / seating / occasion). These
  // only render when the matching public config key is allowlisted; otherwise
  // they stay empty and are never sent.
  accessibilityNeeds: string;
  seatingPreference: string;
  occasion: string;

  // V2 feature flag (`widgetBookingV2`, default OFF). Gates the recovery / Notify
  // / Add-to-Calendar / cross-sell surface. When OFF the fast 2–3 step booking
  // path is byte-for-byte unchanged. The flag never travels in the public config
  // (feature flags are private — #218/#267/#289); the embed sets it explicitly.
  widgetBookingV2: boolean;

  // UI
  loading: boolean;
  error: string | null;
  configLoaded: boolean;
}

export const STEP_ORDER: BookingStep[] = ['service', 'datetime', 'contact', 'payment', 'confirm'];

export function getStepIndex(step: BookingStep): number {
  return STEP_ORDER.indexOf(step);
}

/** The currently-selected PublicEvent, or null when none is chosen. */
export function selectedEvent(state: BookingState): PublicEvent | null {
  if (!state.selectedEventId) return null;
  return state.events.find((e) => e.id === state.selectedEventId) ?? null;
}

/**
 * MVP-4 — true when the service step should show the two-path chooser instead of
 * a booking form: there are events to choose between AND the guest has not yet
 * picked a path. With no events the chooser never shows (the flow behaves as the
 * 'table' path); once a path is chosen the chooser is replaced by that path's UI.
 */
export function showBookingModeChooser(state: BookingState): boolean {
  return state.events.length > 0 && state.bookingMode === null;
}

/**
 * MVP-4 — the state patch that returns the service step to the two-path chooser
 * (the "‹ Back" affordance). Clears `bookingMode` back to null; when leaving the
 * EVENTS path it also clears any event selection via the same fields the manual
 * "deselect" path (`_onEventSelect(null)`) resets, so re-entering starts clean.
 * Leaving the TABLE path only clears the mode (there is no selection to drop).
 */
export function exitBookingMode(state: BookingState): Partial<BookingState> {
  if (state.bookingMode === 'events') {
    return {
      bookingMode: null,
      selectedEventId: null,
      selectedEventName: null,
      selectedTierId: null,
      ticketQuote: null,
      ticketAvailability: null,
      ticketQuoteError: null,
      couponCode: '',
      couponApplied: null,
      couponError: null,
      couponApplying: false,
      autoDeal: null,
      ticketDiscountLine: null,
      menuPdfBusy: false,
      menuPdfError: null,
      ticketLines: [],
      experienceOccurrences: [],
      selectedOccurrenceId: null,
      selectedOccurrenceDate: null,
      // Event selection writes the event's date into selectedDate — clear it
      // (and the slot/availability it invalidates) so switching to the table
      // path never lands on an event's date with no availability fetched.
      selectedDate: null,
      selectedSlot: null,
      selectedSlotEnd: null,
      availableSlots: [],
    };
  }
  return { bookingMode: null };
}

/**
 * True when the selected event sells prepaid tickets. The ticketed purchase flow
 * diverges from a normal booking: the `datetime` step is skipped (the event's
 * date/time is fixed), payment is always required (the full cover is paid up
 * front), and submit goes through /ticket/checkout + /ticket/verify rather than
 * the normal book + deposit path. All new behavior is gated on this predicate so
 * non-ticketed events and ordinary bookings are completely unaffected.
 */
export function isTicketedFlow(state: BookingState): boolean {
  return !!selectedEvent(state)?.ticketing;
}

/**
 * #130 — true when the selected event is a recurring "standard experience"
 * series. A recurring series is also a ticketed flow (it carries `ticketing`),
 * but unlike a one-off it has NO fixed date: the guest must pick a bookable
 * occurrence in the datetime step, which resolves the opaque occurrence id the
 * quote/checkout/book sites send. So every step-machine gate that assumes the
 * ticketed flow has a fixed date (and therefore skips datetime) must exclude the
 * recurring case. One-off events are `recurring: undefined` ⇒ false ⇒ unchanged.
 */
export function isRecurringFlow(state: BookingState): boolean {
  return !!selectedEvent(state)?.recurring;
}

/**
 * r93 X2 — the selected event sells TIERS (#652 per-tier steppers) and the
 * guest has not put a ticket in the basket yet. Such a basket has nothing to
 * price: it is not quoted, and it cannot leave the service step.
 */
export function isTieredBasketEmpty(state: BookingState): boolean {
  const tiers = selectedEvent(state)?.ticketing?.tiers ?? [];
  return tiers.length > 0 && state.ticketLines.length === 0;
}

/**
 * #947 — true when the selected event is a ONE-OFF (not a recurring series) with
 * a fixed `date` AND a `start` time, ticketed or not. There is nothing for the
 * guest to choose in Date & Time, so the step is skipped. A recurring series
 * still chooses its occurrence there (#130); an event with a date but no start
 * still opens Date & Time (preset to the event's date).
 *
 * G3 — the date and start must be ones `fixedEventSlot` can actually turn into
 * a slot (a real `YYYY-MM-DD`; strict `HH:mm`, 00–23 / 00–59). `11h00`,
 * `11:00:00` or `25:99` never skips, so the skip and the slot cannot disagree
 * (a skip with no slot would reach `/book` with nothing to book).
 */
export function isFixedDateEvent(state: BookingState): boolean {
  const ev = selectedEvent(state);
  return !!ev && !ev.recurring && parseIsoDate(ev.date) !== null && parseHhmm(ev.start) !== null;
}

/**
 * #947 — the single answer to "is Date & Time skipped?", shared by the step
 * machine and the host's sidebar. A ticketed one-off keeps its pre-#947 skip
 * byte-for-byte (`isTicketedFlow && !isRecurringFlow`); a fixed-date one-off
 * now skips too.
 */
export function skipsDatetime(state: BookingState): boolean {
  return (isTicketedFlow(state) && !isRecurringFlow(state)) || isFixedDateEvent(state);
}

/**
 * #947 — the slot a fixed-date NON-ticketed event books: the event's own date +
 * start as a NAIVE venue wall clock (`2026-12-04T11:00:00`), ending at the
 * event's `end` or after the venue's default duration. Naive on purpose (G3):
 * the server anchors a naive slot to the venue zone (#911, both ends, before the
 * slot pre-check), so no client offset maths — which an old browser without
 * Intl `longOffset` gets wrong — is on the booking path. The ordinary `/book`
 * submit carries it with the eventId, so pacing / online seats / blocks are
 * still enforced server-side on exactly this time.
 *
 * An end at/before the start wraps past midnight; an end EQUAL to the start (or
 * a malformed one) is treated as no end ⇒ the venue default, never +24h.
 * Null for anything else (a ticketed event checks out through /ticket/*).
 */
export function fixedEventSlot(
  state: BookingState,
): Pick<BookingState, 'selectedDate' | 'selectedSlot' | 'selectedSlotEnd'> | null {
  if (!isFixedDateEvent(state) || isTicketedFlow(state)) return null;
  const ev = selectedEvent(state)!;
  const startMin = parseHhmm(ev.start)!;
  const endMin = parseHhmm(ev.end);
  let length = state.defaultDuration > 0 ? state.defaultDuration : 90;
  if (endMin !== null && endMin !== startMin) {
    length = endMin > startMin ? endMin - startMin : endMin - startMin + 1440;
  }
  const start = naiveLocalIso(ev.date, ev.start!);
  const end = naiveLocalIso(ev.date, ev.start!, length);
  if (!start || !end) return null;
  return { selectedDate: ev.date, selectedSlot: start, selectedSlotEnd: end };
}

/**
 * #947 — the summary's view of a fixed-date booking: the naive wire slot
 * anchored to the venue offset, so it formats as the venue's wall clock for a
 * guest in any zone. Falls back to the stored value if Intl cannot resolve the
 * offset. Null when the flow is not a fixed non-ticketed event.
 */
export function fixedEventDisplaySlot(state: BookingState): { start: string; end: string } | null {
  if (!isFixedDateEvent(state) || isTicketedFlow(state) || !state.selectedSlot) return null;
  return {
    start: anchorForDisplay(state.selectedSlot, state.timezone) ?? state.selectedSlot,
    end: state.selectedSlotEnd
      ? (anchorForDisplay(state.selectedSlotEnd, state.timezone) ?? state.selectedSlotEnd)
      : '',
  };
}

/** #947 — "Fri 4 Dec, 11:00" for a fixed-date event, '' otherwise. */
export function fixedEventWhen(state: BookingState): string {
  if (!isFixedDateEvent(state)) return '';
  const ev = selectedEvent(state)!;
  return formatFixedWhen(ev.date, ev.start!);
}

/**
 * R10.4 — true when a date has been chosen, availability has finished loading,
 * and no bookable slot came back. This is the trigger for the empty-state
 * recovery block (alternate times / sections / try-another-date / Join Notify /
 * sibling venue) instead of the bare "No times available" string.
 *
 * A slot grid that contains only `available: false` slots also counts as empty
 * — there is nothing the guest can pick.
 */
export function isAvailabilityEmpty(state: BookingState): boolean {
  if (!state.selectedDate || state.loading) return false;
  return !state.availableSlots.some((s) => s.available);
}

/**
 * Whether the rich recovery surface should render. Gated on the V2 flag so the
 * fast path is untouched when off: with the flag OFF an empty date keeps the
 * original "No times available for this date." string.
 */
export function showRecoveryUI(state: BookingState): boolean {
  return state.widgetBookingV2 && isAvailabilityEmpty(state);
}

export function createInitialState(overrides: Partial<BookingState> = {}): BookingState {
  return {
    currentStep: 'service',
    locationId: '',
    calendarId: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    businessName: '',
    theme: null,

    bookingMode: null,
    partySize: 2,
    maxOnlinePartySize: 0,
    contactPhone: '',
    contactEmail: '',
    kidsCount: 0,

    events: [],
    selectedEventId: null,
    selectedEventName: null,

    experienceOccurrences: [],
    occurrencesLoading: false,
    selectedOccurrenceId: null,
    selectedOccurrenceDate: null,

    selectedTierId: null,
    ticketQuote: null,
    ticketAvailability: null,
    ticketQuoteError: null,
    couponCode: '',
    couponApplied: null,
    couponError: null,
    couponApplying: false,
    autoDeal: null,
    ticketDiscountLine: null,
    menuPdfBusy: false,
    menuPdfError: null,
    ticketLines: [],
    ticketReservationId: null,
    ticketDate: null,

    selectedDate: null,
    selectedSlot: null,
    selectedSlotEnd: null,
    availableSlots: [],
    defaultDuration: 90,

    availabilityReason: null,
    alternateTimes: [],
    alternateSections: [],
    nextAvailableDate: null,

    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    notes: '',
    dietaryRestrictions: '',
    highChairNeeded: false,
    dietaryDetails: '',
    consentGiven: false,
    fieldStates: { ...DEFAULT_FIELD_STATES },

    depositCheckout: false,
    depositRequired: false,
    depositType: null,
    depositAmountCents: 0,
    depositReservationId: null,
    depositDate: null,
    depositExpiresAt: null,
    depositAuthorizationUrl: '',
    depositPolicyNote: null,

    requirePayment: false,
    paymentProvider: 'none',
    stripePublicKey: '',
    paymentAmount: 0,
    paymentCurrency: DEFAULT_PAYMENT_CURRENCY,
    paymentIntentId: null,
    paymentClientSecret: null,
    paymentStatus: 'idle',

    depositConfig: null,
    workingHoursSchedules: [],

    ghlOrderId: null,
    ghlPaymentUrl: null,

    idempotencyKey: '',
    appointmentId: null,
    contactId: null,
    confirmationNumber: null,

    notifySubmitting: false,
    notifySubmitted: false,
    notifyError: null,
    siblingVenue: null,
    manageBookingUrl: '',
    accessibilityNeeds: '',
    seatingPreference: '',
    occasion: '',
    widgetBookingV2: false,

    loading: false,
    error: null,
    configLoaded: false,

    ...overrides,
  };
}

/**
 * Ticketed-event purchases always require payment (the full cover is prepaid),
 * regardless of the tenant's deposit config. Normal bookings fall back to the
 * configured `requirePayment` flag.
 *
 * #480 §5.2 — a `depositCheckout` venue must ALSO stop at the payment step even
 * though its `requirePayment` is false (a depositRules-only tenant has no
 * legacy `depositConfig`/`paymentAmount`, so widening `requirePayment` itself
 * would resurrect the 2026-07-27 "Continue silently does nothing" regression,
 * §4.1). With the flag off `depositCheckout` is false for every tenant and this
 * predicate is byte-identical to today.
 *
 * EXPORTED because the host's sidebar must hide/show the payment step on exactly
 * the same condition the step machine routes on. It used to inline
 * `!requirePayment && !isTicketedFlow` for that, which predated the deposit rail
 * — so a `depositCheckout` venue was walked TO a payment step the nav claimed
 * did not exist (no active step highlighted at all). One predicate, one answer.
 */
export function paymentRequired(state: BookingState): boolean {
  return isTicketedFlow(state) || state.depositCheckout || state.requirePayment;
}

/** Step validators — returns true if the step's required data is present */
export function canAdvance(state: BookingState): boolean {
  switch (state.currentStep) {
    case 'service':
      // #130 — a recurring series has no quote yet on the service step (the occId
      // that the quote keys on is only resolved later, in datetime). Gating it on
      // a quote here would strand the series — it could never leave the service
      // step. It advances on party size alone; the occurrence + quote come next.
      if (isRecurringFlow(state)) {
        // r93 X2 — a TIERED series still needs a ticket in the basket.
        return state.partySize > 0 && !isTieredBasketEmpty(state);
      }
      // A sold-out / un-quotable ticket cannot proceed past selection.
      if (isTicketedFlow(state)) {
        return state.partySize > 0 && !!state.ticketQuote && !state.ticketAvailability?.soldOut;
      }
      return state.partySize > 0;
    case 'datetime':
      // #130 — a recurring series uses the datetime step to pick a bookable
      // occurrence (not a slot grid): it advances once an occurrence is chosen
      // and its authoritative quote has resolved (and is not sold out).
      if (isRecurringFlow(state)) {
        return !!state.selectedOccurrenceId && !!state.ticketQuote && !state.ticketAvailability?.soldOut;
      }
      return !!state.selectedDate && !!state.selectedSlot;
    case 'contact':
      return !!state.firstName.trim() && !!state.email.trim();
    case 'payment':
      return !paymentRequired(state) || state.paymentStatus === 'succeeded';
    case 'confirm':
      return false; // terminal step
    default:
      return false;
  }
}

export function nextStep(state: BookingState): BookingStep | null {
  const idx = getStepIndex(state.currentStep);
  // Ticketed flow: the event date/time is fixed, so skip the datetime step.
  // #130 — EXCEPT a recurring series, whose occurrence date is chosen IN datetime,
  // so it must NOT skip it. One-off (`!isRecurringFlow`) keeps the skip unchanged.
  // #947 — a fixed-date (date + start) non-ticketed one-off skips it too.
  if (state.currentStep === 'service' && skipsDatetime(state)) {
    return 'contact';
  }
  // Skip payment step if not required
  if (state.currentStep === 'contact' && !paymentRequired(state)) {
    return 'confirm';
  }
  return STEP_ORDER[idx + 1] ?? null;
}

export function prevStep(state: BookingState): BookingStep | null {
  const idx = getStepIndex(state.currentStep);
  // Ticketed flow: datetime was skipped, so step back to service.
  // #130 — a recurring series did NOT skip datetime, so it steps back there.
  // #947 — a fixed-date event skipped it, so Back returns to the event choice.
  if (state.currentStep === 'contact' && skipsDatetime(state)) {
    return 'service';
  }
  // Skip payment step going backwards
  if (state.currentStep === 'confirm' && !paymentRequired(state)) {
    return 'contact';
  }
  return STEP_ORDER[idx - 1] ?? null;
}

export const bookingContext = createContext<BookingState>('yebo-booking-state');
